import numeral from 'numeral';
// utils
import { localStorageGetItem } from './storage-available';
// locales
import { allLangs, defaultLang } from '../locales';

// ----------------------------------------------------------------------

type InputValue = string | number | null;

// Platform is EUR-only. If multi-currency is ever introduced, switch the
// glyph at this single source and add an FX-conversion layer — never
// swap symbols without converting values.
export const CURRENCY_SYMBOL = '€';

/**
 * NAT-2711 — the gap between the symbol and the figure. A no-break space, so «€ 22.049,40»
 * never wraps into two lines that read as two numbers. `fCurrencySymbol` (an input adornment)
 * keeps its plain space.
 */
const NBSP = '\u00a0';

/** «€ <figure>» with the no-break gap, for a figure that is already formatted. */
export const withEuro = (figure: string) => `${CURRENCY_SYMBOL}${NBSP}${figure}`;

/** The empty fallback of a euro summary card. */
export const EURO_ZERO = withEuro('0');

export function fNumber(number: InputValue) {
  return numeral(number).format();
}

export function fCurrencySymbol(year?: number | string, currentLang?: string): string {
  const yearNumber = Number(year);
  return year ? `${CURRENCY_SYMBOL} ${yearNumber}` : `${CURRENCY_SYMBOL} `;
}

export function fCurrency(number: InputValue, currentLang?: string) {
  const lang = currentLang || getCurrentLang();
  const isCurrentLangEn = lang === 'en';

  // only make null and undefined as falsy values
  const formattedNumber =
    number !== null && number !== undefined
      ? `${CURRENCY_SYMBOL}${NBSP}${`${new Intl.NumberFormat(isCurrentLangEn ? 'en-US' : 'de-DE', {
          style: 'currency',
          currency: 'EUR',
        }).format(Number(number))}`
          .replace(/€/g, '')
          // `trim` strips U+00A0: de-DE puts one between the figure and the symbol.
          .trim()}`
      : '';
  const replaceValue = isCurrentLangEn ? '.00' : ',00';
  return result(formattedNumber, replaceValue, lang);
}

export function fCurrencyFormat(number: InputValue, currentLang?: string) {
  const lang = currentLang || getCurrentLang();
  const isCurrentLangEn = lang === 'en';
  const formattedNumber = number
    ? `${`${new Intl.NumberFormat(isCurrentLangEn ? 'en-US' : 'de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(Number(number))}`
        .replace(/€/g, '')
        .trim()}`
    : '';
  const replaceValue = isCurrentLangEn ? '.00' : ',00';
  return result(formattedNumber, replaceValue, lang);
}

/**
 * NAT-2820 — fCurrencyFormat for a column where a real 0 is a fact: `fCurrencyFormat` blanks every
 * falsy input, so a settled 0 reads as a missing field. Absent (null/undefined) is still '' (the
 * caller picks its own placeholder), a real 0 is bare «0,00» («0» in English) like the rest of
 * the column, and every other amount goes through `fCurrencyFormat` untouched.
 */
export function fCurrencyFormatKeepZero(number: number | null | undefined, currentLang?: string) {
  if (number === null || number === undefined) return '';
  if (number === 0) return (currentLang || getCurrentLang()) === 'en' ? '0' : '0,00';
  return fCurrencyFormat(number, currentLang);
}

export function fCurrencyAfterSymbol(number: InputValue, currentLang?: string) {
  const lang = currentLang || getCurrentLang();
  const isCurrentLangEn = lang === 'en';

  const format = number
    ? `${`${new Intl.NumberFormat(isCurrentLangEn ? 'en-US' : 'de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(Number(number))}`.replace(/€/g, '')}${CURRENCY_SYMBOL}${NBSP}`
    : '';

  return result(format, '.00', lang);
}

/**
 * NAT-2409. Formatted through `Intl` on the same locale rule as `fCurrency`, not through
 * `numeral` — which has no locale registered and so always emitted a dot. An Italian screen
 * showing both put "€ 90.260,89" next to "20.09%" on one row, two decimal separators in the
 * same sentence.
 *
 * `currentLang` is optional and behaves like `fCurrency`'s: a caller that already knows the
 * language passes it, rather than each formatter reaching into storage on its own and the
 * amount and the percentage disagreeing for a render after a language switch.
 */
export function fPercent(number: InputValue, currentLang?: string) {
  // Anything that is not a finite number renders as nothing, which is what the old falsy
  // check did for every absent value. `Number(undefined)` is NaN and reaches here from at
  // least one live call site (`grape-type-row.tsx`), where a blank cell is right and "NaN%"
  // is not. `0` is deliberately outside this guard: a blank where a percentage belongs reads
  // as missing data, which is a different claim from zero.
  const value = Number(number);
  if (number === null || number === undefined || number === '' || !Number.isFinite(value)) {
    return '';
  }

  const lang = currentLang || getCurrentLang();
  const isEn = lang === 'en';

  // The SAME locale `fCurrency` uses, `de-DE` and not `it-IT`, which reads odd until you
  // check the grouping: Italian CLDR sets `minimumGroupingDigits: 2`, so it-IT leaves four
  // digits ungrouped — "1005,00%" beside "€ 1.005,00". Matching the currency formatter
  // exactly is the entire point of this function, separator and grouping alike.
  const formatted = new Intl.NumberFormat(isEn ? 'en-US' : 'de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(value)
    // `Intl` keeps the sign on a value that ROUNDS to zero (-0.004 → "-0,00"); `numeral` did
    // not. Testing `value === 0` misses it, because the sign survives the rounding, not the
    // value. A rounding artefact should not read as a negative.
    .replace(/^-(?=0(?:[.,]0+)?$)/, '');

  // The key must follow the locale that produced the string, not the language: `result`
  // strips a trailing zero-decimal for anything that is not Italian, and with `de-DE` output
  // that decimal is a comma. Keying both off `isEn` keeps them from ever disagreeing — with
  // `'.00'` against `de-DE`, a third language would strip the FIRST dot in "1.000.000,00"
  // and silently render a hundredth of the number.
  return result(`${formatted}%`, isEn ? '.00' : ',00', lang);
}

export function fShortenNumber(number: InputValue) {
  const format = number ? numeral(number).format('0.00a') : '';

  return result(format, '.00', getCurrentLang());
}

export function fData(number: InputValue) {
  const format = number ? numeral(number).format('0.0 b') : '';

  return result(format, '.0', getCurrentLang());
}

function result(format: string, key = '.00', currentLang?: string) {
  const isInteger = format.includes(key);
  return isInteger && currentLang !== 'it' ? format.replace(key, '') : format;
}

export function fTotalTableResult({
  tableLength = 0,
  totalPages = 0,
  rowsPerPage = 0,
}: {
  tableLength: number;
  totalPages: number;
  rowsPerPage: number;
}) {
  return totalPages * rowsPerPage > 10 ? totalPages * rowsPerPage : tableLength;
}

// ----------------------------------------------------------------------

/**
 * Get the current selected locale from localStorage
 */
export function getCurrentLang(): string {
  const langStorage = localStorageGetItem('i18nextLng');
  const currentLang = allLangs.find((lang) => lang.value === langStorage) || defaultLang;
  return currentLang.value;
}
